<template>
  <div class="panel-invitations">
    <div class="panel-toolbar">
      <p class="panel-toolbar-meta">
        Generá códigos con un rol asignado para el registro de nuevos clientes.
        <router-link to="/panel/roles" class="ms-2 link-roles">Gestionar roles →</router-link>
      </p>
      <div class="panel-toolbar-actions">
        <button class="btn btn-primary" @click="openCodeModal()">
          <i class="fas fa-plus me-2"></i>Nuevo código
        </button>
      </div>
    </div>

    <div v-if="loadingCodes" class="text-center py-5">
        <div class="spinner-border text-primary"></div>
      </div>
      <template v-else>
        <div class="panel-mobile-cards d-md-none">
          <article v-for="item in codes" :key="`m-${item.id}`" class="panel-data-card">
            <header class="panel-data-card__head">
              <div>
                <p class="panel-data-card__title mb-0"><code>{{ item.code }}</code></p>
                <div class="panel-data-card__subtitle">
                  <span
                    v-if="item.role_label"
                    class="role-tag"
                    :style="{ backgroundColor: item.role_color || '#adb5bd' }"
                  >{{ item.role_label }}</span>
                  <span v-else class="text-muted">Sin rol</span>
                </div>
              </div>
              <span class="badge" :class="codeBadgeClass(item)">{{ codeStatusLabel(item) }}</span>
            </header>
            <div class="panel-data-card__rows">
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Usos</span>
                <span class="panel-data-card__value">{{ item.current_uses }} / {{ item.max_uses }}</span>
              </div>
              <div class="panel-data-card__row">
                <span class="panel-data-card__label">Vence</span>
                <span class="panel-data-card__value">{{ formatDate(item.expires_at) }}</span>
              </div>
              <div v-if="item.description" class="panel-data-card__row">
                <span class="panel-data-card__label">Descripción</span>
                <span class="panel-data-card__value">{{ item.description }}</span>
              </div>
            </div>
            <footer class="panel-data-card__actions">
              <button class="btn btn-sm btn-outline-light" title="Editar" @click="openCodeModal(item)">
                <i class="fas fa-pen me-1"></i>Editar
              </button>
              <button
                v-if="item.is_active"
                class="btn btn-sm btn-outline-warning"
                title="Desactivar"
                @click="toggleCodeActive(item, false)"
              >
                <i class="fas fa-ban me-1"></i>Desactivar
              </button>
              <button
                v-else
                class="btn btn-sm btn-outline-success"
                title="Activar"
                @click="toggleCodeActive(item, true)"
              >
                <i class="fas fa-check me-1"></i>Activar
              </button>
              <button class="btn btn-sm btn-outline-danger" title="Eliminar" @click="confirmDeleteCode(item)">
                <i class="fas fa-trash me-1"></i>Eliminar
              </button>
            </footer>
          </article>
          <div v-if="codes.length === 0" class="panel-data-card text-center text-muted py-4">No hay códigos creados</div>
        </div>

        <div class="table-responsive panel-table-wrap d-none d-md-block">
        <table class="table table-dark table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>Código</th>
              <th>Rol</th>
              <th>Usos</th>
              <th>Vence</th>
              <th>Estado</th>
              <th>Descripción</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in codes" :key="item.id">
              <td><code class="fs-6">{{ item.code }}</code></td>
              <td>
                <span
                  v-if="item.role_label"
                  class="role-tag"
                  :style="{ backgroundColor: item.role_color || '#adb5bd' }"
                >{{ item.role_label }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td>{{ item.current_uses }} / {{ item.max_uses }}</td>
              <td>{{ formatDate(item.expires_at) }}</td>
              <td>
                <span class="badge" :class="codeBadgeClass(item)">{{ codeStatusLabel(item) }}</span>
              </td>
              <td class="text-muted small">{{ item.description || '—' }}</td>
              <td class="text-end text-nowrap">
                <button class="btn btn-sm btn-outline-light me-1" title="Editar" @click="openCodeModal(item)">
                  <i class="fas fa-pen"></i>
                </button>
                <button
                  v-if="item.is_active"
                  class="btn btn-sm btn-outline-warning me-1"
                  title="Desactivar"
                  @click="toggleCodeActive(item, false)"
                >
                  <i class="fas fa-ban"></i>
                </button>
                <button
                  v-else
                  class="btn btn-sm btn-outline-success me-1"
                  title="Activar"
                  @click="toggleCodeActive(item, true)"
                >
                  <i class="fas fa-check"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" title="Eliminar" @click="confirmDeleteCode(item)">
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
            <tr v-if="codes.length === 0">
              <td colspan="7" class="text-center text-muted py-4">No hay códigos creados</td>
            </tr>
          </tbody>
        </table>
        </div>
      </template>

    <!-- Modal código -->
    <div v-if="showCodeModal" class="panel-modal-overlay" @click.self="showCodeModal = false">
      <div class="panel-modal">
        <h3>{{ editingCode ? 'Editar código' : 'Nuevo código de invitación' }}</h3>
        <form @submit.prevent="saveCode">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">Rol asignado</label>
              <select v-model="codeForm.assigned_role_id" class="form-select" required>
                <option v-for="r in activeRoles" :key="r.id" :value="r.id">
                  {{ r.label }}
                </option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label">Máx. usos</label>
              <input v-model.number="codeForm.max_uses" type="number" min="1" class="form-control" required />
            </div>
            <div class="col-md-6">
              <label class="form-label">Vencimiento</label>
              <input v-model="codeForm.expires_at" type="datetime-local" class="form-control" required />
            </div>
            <div class="col-12">
              <label class="form-label">Descripción (opcional)</label>
              <input v-model="codeForm.description" class="form-control" maxlength="255" placeholder="Ej: Campaña growshops Q2" />
            </div>
            <div class="col-12">
              <div class="form-check">
                <input v-model="codeForm.is_active" class="form-check-input" type="checkbox" id="codeActive" />
                <label class="form-check-label" for="codeActive">Activo</label>
              </div>
            </div>
          </div>
          <div class="panel-modal-actions">
            <button type="button" class="btn btn-secondary" @click="showCodeModal = false">Cancelar</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'Guardando...' : 'Guardar' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useUnsavedChangesGuard, useFormDirty } from '@/composables/useUnsavedChangesGuard'
import {
  getCustomerRoles,
  getInvitationCodes,
  createInvitationCode,
  updateInvitationCode,
  deleteInvitationCode,
  activateInvitationCode,
  deactivateInvitationCode,
} from '@/services/invitations'

const toast = useToast()
const roles = ref([])
const codes = ref([])
const loadingCodes = ref(true)
const saving = ref(false)

const showCodeModal = ref(false)
const editingCode = ref(null)
const codeForm = ref(emptyCodeForm())

const codeFormDirty = useFormDirty(codeForm)
const dirty = computed(() => showCodeModal.value && codeFormDirty.dirty.value)

useUnsavedChangesGuard({
  dirty,
  saveFn: () => saveCode(),
  message: 'El formulario del código de invitación tiene cambios sin guardar. Si salís ahora, se van a perder.',
})

const activeRoles = computed(() => roles.value.filter(r => r.is_active))

function emptyCodeForm() {
  const d = new Date()
  d.setMonth(d.getMonth() + 3)
  return {
    assigned_role_id: null,
    max_uses: 1,
    expires_at: toLocalInput(d.toISOString()),
    description: '',
    is_active: true,
  }
}

function toLocalInput(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function toIso(local) {
  return local ? new Date(local).toISOString() : null
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })
}

function codeStatusLabel(item) {
  if (!item.is_active) return 'Inactivo'
  if (!item.is_valid) return 'Expirado / agotado'
  return 'Activo'
}

function codeBadgeClass(item) {
  if (!item.is_active) return 'bg-secondary'
  if (!item.is_valid) return 'bg-danger'
  return 'bg-success'
}

const loadRoles = async () => {
  try {
    const data = await getCustomerRoles({ active_only: 'true' })
    roles.value = Array.isArray(data) ? data : (data.results || [])
  } catch {
    roles.value = []
  }
}

const loadCodes = async () => {
  loadingCodes.value = true
  try {
    const data = await getInvitationCodes()
    codes.value = Array.isArray(data) ? data : (data.results || [])
  } catch {
    toast.error('No se pudieron cargar los códigos')
  } finally {
    loadingCodes.value = false
  }
}

const openCodeModal = (item = null) => {
  editingCode.value = item
  if (item) {
    codeForm.value = {
      assigned_role_id: item.assigned_role || activeRoles.value.find(r => r.slug === item.role_slug)?.id || null,
      max_uses: item.max_uses,
      expires_at: toLocalInput(item.expires_at),
      description: item.description || '',
      is_active: item.is_active,
    }
  } else {
    codeForm.value = emptyCodeForm()
    if (activeRoles.value.length) {
      codeForm.value.assigned_role_id = activeRoles.value[0].id
    }
  }
  codeFormDirty.markClean()
  showCodeModal.value = true
}

const saveCode = async () => {
  saving.value = true
  try {
    const payload = {
      assigned_role_id: codeForm.value.assigned_role_id,
      max_uses: codeForm.value.max_uses,
      expires_at: toIso(codeForm.value.expires_at),
      description: codeForm.value.description,
      is_active: codeForm.value.is_active,
    }
    if (editingCode.value) {
      await updateInvitationCode(editingCode.value.id, payload)
      toast.success('Código actualizado')
    } else {
      const created = await createInvitationCode(payload)
      toast.success(`Código creado: ${created.code}`)
    }
    showCodeModal.value = false
    await loadCodes()
  } catch (e) {
    const err = e.response?.data
    toast.error(err?.detail || err?.assigned_role_id?.[0] || err?.expires_at?.[0] || 'Error al guardar código')
  } finally {
    saving.value = false
  }
}

const toggleCodeActive = async (item, active) => {
  try {
    if (active) {
      await activateInvitationCode(item.id)
    } else {
      await deactivateInvitationCode(item.id)
    }
    toast.success(active ? 'Código activado' : 'Código desactivado')
    await loadCodes()
  } catch (e) {
    toast.error(e.response?.data?.error || 'No se pudo cambiar el estado')
  }
}

const confirmDeleteCode = async (item) => {
  if (!confirm(`¿Eliminar el código ${item.code}?`)) return
  try {
    await deleteInvitationCode(item.id)
    toast.success('Código eliminado')
    await loadCodes()
  } catch {
    toast.error('No se pudo eliminar el código')
  }
}

onMounted(async () => {
  await loadRoles()
  await loadCodes()
})
</script>

<style scoped>
.nav-tabs .nav-link {
  color: rgba(255, 255, 255, 0.65);
  border: none;
  background: transparent;
  cursor: pointer;
}
.nav-tabs .nav-link.active {
  color: var(--primary-color);
  border-bottom: 2px solid var(--primary-color);
  background: transparent;
}
</style>
